import React, { useEffect, useMemo, useState } from "react";
import { GripVertical } from "lucide-react";

import { Button, Table } from "@/Components/ui";
import { cn } from "@/lib/utils";

export type DraggableTableColumn<T> = {
  key: string;
  header: React.ReactNode;
  className?: string;
  render: (row: T) => React.ReactNode;
};

type DraggableTableProps<T> = {
  data: T[];
  getRowId: (row: T) => string | number;
  columns: DraggableTableColumn<T>[];
  onReorder?: (rows: T[]) => void;
  onSaveOrder?: (rows: T[]) => Promise<void> | void;
  onSaveError?: (error: unknown, rows: T[]) => void;
  saveLabel?: string;
  savingLabel?: string;
  emptyText?: string;
  className?: string;
};

function DraggableTable<T>({
  data,
  getRowId,
  columns,
  onReorder,
  onSaveOrder,
  onSaveError,
  saveLabel = "순서 저장",
  savingLabel = "저장 중...",
  emptyText = "데이터가 없습니다.",
  className,
}: DraggableTableProps<T>) {
  const [rows, setRows] = useState<T[]>(data);
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setRows(data);
  }, [data]);

  const rowIds = useMemo(() => rows.map((row) => getRowId(row)), [rows, getRowId]);

  const handleDrop = (toIndex: number) => {
    if (draggingIndex === null || draggingIndex === toIndex) {
      setDraggingIndex(null);
      setHoveredIndex(null);
      return;
    }

    const nextRows = [...rows];
    const [moved] = nextRows.splice(draggingIndex, 1);
    nextRows.splice(toIndex, 0, moved);

    setRows(nextRows);
    setDraggingIndex(null);
    setHoveredIndex(null);
    onReorder?.(nextRows);
  };

  const saveOrder = async () => {
    if (!onSaveOrder) return;
    setIsSaving(true);
    try {
      await onSaveOrder(rows);
    } catch (error) {
      onSaveError?.(error, rows);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={cn("space-y-3", className)}>
      {onSaveOrder ? (
        <div className="flex justify-end">
          <Button color="success" size="sm" onClick={saveOrder} disabled={isSaving}>
            {isSaving ? savingLabel : saveLabel}
          </Button>
        </div>
      ) : null}

      <div className="w-full overflow-x-auto">
        <Table className="w-full mb-0 border-y border-slate-200">
          <thead className="bg-slate-50 border-b border-slate-200 text-slate-500">
            <tr>
              <th className="h-12 w-12 px-2 text-center align-middle font-medium">순서</th>
              {columns.map((column) => (
                <th key={column.key} className="h-12 px-4 text-left align-middle font-medium">
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 1} className="p-4 text-center text-slate-500">
                  {emptyText}
                </td>
              </tr>
            ) : (
              rows.map((row, index) => (
                <tr
                  key={String(getRowId(row))}
                  className={cn(
                    "transition-colors",
                    draggingIndex === index ? "opacity-50" : "",
                    hoveredIndex === index ? "bg-blue-50/60" : "hover:bg-slate-50/80"
                  )}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setHoveredIndex(index);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    handleDrop(index);
                  }}
                >
                  <td className="p-2 align-middle text-center">
                    <button
                      type="button"
                      className="inline-flex cursor-grab items-center justify-center rounded border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-100"
                      draggable
                      onDragStart={() => {
                        setDraggingIndex(index);
                        setHoveredIndex(index);
                      }}
                      onDragEnd={() => {
                        setDraggingIndex(null);
                        setHoveredIndex(null);
                      }}
                      aria-label={`reorder-${String(getRowId(row))}`}
                    >
                      <GripVertical size={16} />
                    </button>
                  </td>
                  {columns.map((column) => (
                    <td key={`${String(getRowId(row))}-${column.key}`} className={cn("p-4 align-middle text-slate-700", column.className)}>
                      {column.render(row)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      <input type="hidden" value={rowIds.join(",")} readOnly />
    </div>
  );
}

export default DraggableTable;
